import { useEffect, useState } from "react";
import { useAuth } from "src/contexts/auth";
import { fetchWithToken } from "src/hooks/useFetch";
import { SpatialRun } from "../types";
import {
  RunParameterWell,
  tilesByWellFromRunParameters,
} from "./run-parameter-well-tiles";

interface RunParametersResponse {
  Tiles: string[];
  Wells?: RunParameterWell[];
}

export interface RunTileLabelsState {
  tileLabels: string[];
  // Per-well tile names in Wells[].Tiles order. This is the index space for
  // Molecule per-well arrays. Distinct from tileLabels, which follows the
  // flat Tiles walk.
  wellTilesByLocation: Map<string, string[]>;
  isLoading: boolean;
  error: Error | null;
}

export const useRunTileLabels = (run?: SpatialRun): RunTileLabelsState => {
  const { getAccessTokenSilently } = useAuth();
  const [tileLabels, setTileLabels] = useState<string[]>([]);
  const [wellTilesByLocation, setWellTilesByLocation] = useState<
    Map<string, string[]>
  >(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchTileLabels = async () => {
      if (!run?.runID) {
        if (!cancelled) {
          setTileLabels([]);
          setWellTilesByLocation(new Map());
          setIsLoading(false);
          setError(null);
        }
        return;
      }

      if (!cancelled) {
        setIsLoading(true);
        setError(null);
      }

      try {
        const response: RunParametersResponse = await fetchWithToken(
          getAccessTokenSilently,
          "/run/runParameters",
          {
            method: "POST",
            body: JSON.stringify({
              RunID: run.runID,
              InstrumentID: run.instrumentID,
              InstrumentName: run.instrumentName,
              InstrumentSerialNumber: run.instrumentSerialNumber,
              TenantID: run.tenantID,
            }),
          }
        );

        if (!cancelled) {
          if (!response || !Array.isArray(response.Tiles)) {
            console.error(
              "runParameters response missing Tiles array:",
              response
            );
            setTileLabels([]);
            setWellTilesByLocation(new Map());
            setError(new Error("runParameters response missing Tiles array"));
            setIsLoading(false);
            return;
          }
          setTileLabels(response.Tiles);
          setWellTilesByLocation(tilesByWellFromRunParameters(response.Wells));
          setError(null);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Failed to fetch tile labels:", err);
        if (!cancelled) {
          setTileLabels([]);
          setWellTilesByLocation(new Map());
          setError(
            err instanceof Error
              ? err
              : new Error("Failed to fetch tile labels")
          );
          setIsLoading(false);
        }
      }
    };

    fetchTileLabels();

    return () => {
      cancelled = true;
    };
  }, [
    run?.runID,
    run?.instrumentID,
    run?.instrumentName,
    run?.instrumentSerialNumber,
    run?.tenantID,
    getAccessTokenSilently,
  ]);

  return { tileLabels, wellTilesByLocation, isLoading, error };
};
